<template>
    <DsModal
        class="legacy-email-modal"
        :is-open="isOpen"
        show-close
        size="med"
        :title="legacyEmailTitle"
        @close="closeModal"
    >
        <template #header>
            <DsFilledButton data-qa="edit-button" @click="editOrCreateLegacyEmail">
                <template #default>
                    {{ actionButtonLabel }}
                </template>
                <template #trailing>
                    <DsIcon name="external-link" />
                </template>
            </DsFilledButton>
        </template>

        <template #default>
            <div class="goal-summary">
                <div class="goal-summary-text">
                    {{ $t('funnel.edit.campaign.legacy.email.summary.text') }}
                </div>
            </div>
        </template>
    </DsModal>
</template>

<script>
import { utils } from '../../../../utils/legacy-utils';
import {
    createConfirmationEmail,
    updateConfirmationEmail,
} from '../legacyEmail/legacy.confirm.email.api';
import {
    findParent,
    getCellById,
} from '../../../../utils/cell/automation-cell-utils';
import {
    GOALS,
    PROCESS_EMAIL_CONFIRM,
} from '../../../../utils/editor.constants';
import { findOutgoingCells } from '../../../../utils/cell/automation-edge-utils';

export const EVENT_READY_TOGGLE_CHANGED = 'confirm-email-ready-toggle-changed';
export const EVENT_EDITOR_LOADED = 'email-editor-loaded';
export const EVENT_CONFIG_DATA = 'load-data';
export const EVENT_SAVE_CONFIG_DATA = 'confirm-email-editor-save';
export const EVENT_RENAME = 'confirm-email-rename';

export default {
    props: {
        cells: {
            type: Array,
            default: () => ([]),
        },
        cell: {
            type: Object,
            default: () => ({
                value: {
                    marketingEmailId: '0',
                    confirmLinkId: '0',
                    ready: '0',
                },
            }),
        },

        isOpen: {
            type: Boolean,
            required: true,
        },
    },
    emits: ['close-modal', 'update-cell-value', 'update-cell-name'],

    data() {
        return {
            loading: false,
            marketingEmailId: '0',
            confirmLinkId: '0',
            editorInstance: null,
            isReady: false,
            localCell: { ...this.cell },
        };
    },

    computed: {
        actionButtonLabel() {
            return this.$t('funnel.edit.campaign.legacy.email.edit');
        },
        legacyEmailTitle() {
            return this.cell.value?.name ?? this.$t('funnel.edit.goal.emailConfirm.flow');
        },

        configuredEmail() {
            return this.marketingEmailId !== '0';
        },
    },

    watch: {
        cell: {
            handler() {
                this.localCell = { ...this.cell };
            },
            deep: true,
        },
    },

    mounted() {
        this.addEventListeners();
        this.name = this.localCell?.value?.name || this.$t('funnel.flowItemType.emailConfirm');
        this.marketingEmailId = utils.removeLongSuffix(this.localCell?.value?.marketingEmailId) || '0';
        this.confirmLinkId = utils.removeLongSuffix(this.localCell?.value?.confirmLinkId) || '0';
        this.isReady = this.localCell.value?.ready === '1' ?? false;
    },

    unmounted() {
        this.removeEventListeners();
    },
    methods: {
        addEventListeners() {
            window.addEventListener(EVENT_READY_TOGGLE_CHANGED, this.readyToggleChanged);
            window.addEventListener(EVENT_SAVE_CONFIG_DATA, this.saveConfigData);
            window.addEventListener(EVENT_EDITOR_LOADED, this.editorReady);
            window.addEventListener(EVENT_RENAME, this.rename);
        },

        removeEventListeners() {
            window.removeEventListener(EVENT_READY_TOGGLE_CHANGED, this.readyToggleChanged);
            window.removeEventListener(EVENT_SAVE_CONFIG_DATA, this.saveConfigData);
            window.removeEventListener(EVENT_EDITOR_LOADED, this.editorReady);
            window.removeEventListener(EVENT_RENAME, this.rename);
        },

        editorReady() {
            this.postConfigMessage();
        },

        readyToggleChanged() {
            this.isReady = !this.isReady;
            this.saveCell();

            this.toggleReadyParentCell();

            this.initializeEmailConfirmationGoal();
        },

        async saveConfigData({ detail }) {
            const legacySaveEmailCommand = {
                fromKey: detail.fromKey,
                fromName: detail.fromName,
                fromEmail: detail.fromEmail,
                toKey: detail.toKey,
                toName: detail.toName,
                toEmail: detail.toEmail,
                subject: detail.subject,
                textBody: detail.textBody,
                htmlBody: detail.htmlBody,
                isCodeBuilder: detail.isCodeBuilder,
            };

            await updateConfirmationEmail(this.marketingEmailId, legacySaveEmailCommand);
        },

        postConfigMessage() {
            this.editorInstance.postMessage({
                eventType: EVENT_CONFIG_DATA,
                data: {
                    cellName: this.name,
                    isReady: this.isReady,
                },
            }, '*');
        },

        closeModal() {
            if (this.editorInstance && !this.editorInstance.closed) {
                this.editorInstance.focus();
            } else {
                if (this.configuredEmail) {
                    this.saveCell();
                }

                this.removeEventListeners();
                this.$emit('close-modal');
            }
        },

        async editOrCreateLegacyEmail() {
            try {
                if (!this.configuredEmail) {
                    const emailTitle = this.legacyEmailTitle;

                    const { confirmLinkId, marketingEmailId } = await createConfirmationEmail(emailTitle);

                    this.confirmLinkId = confirmLinkId;
                    this.marketingEmailId = marketingEmailId;
                    this.updateCellValue();
                    this.initializeEmailConfirmationGoal(confirmLinkId);
                }

                // sync confirmLinkId (needed if automation created and copied over from Polymer, but opened in Konva)
                // only required to maintain backwards compatibility with Polymer
                if (this.confirmLinkId) {
                    this.initializeEmailConfirmationGoal(this.confirmLinkId);
                }

                this.editEmail();
            } catch (error) {
                this.$error({
                    message: this.$t('funnel.confirm.email.create.error', { error }),
                    bottom: true,
                });
            }
        },

        updateCellValue() {
            this.localCell.value.confirmLinkId = this.confirmLinkId;
            this.localCell.value.marketingEmailId = this.marketingEmailId;
            this.localCell.value.ready = this.isReady ? '1' : '0';
        },

        editEmail() {
            if (this.editorInstance && !this.editorInstance.closed) {
                this.editorInstance.focus();
            } else {
                const params = new URLSearchParams({
                    contentType: PROCESS_EMAIL_CONFIRM,
                    parentId: this.marketingEmailId,
                    emailTitle: this.legacyEmailTitle,
                    confirmLinkId: this.confirmLinkId,
                });

                const url = `/app/emailConfirmFunnel/standalone/editor?${params.toString()}`;

                this.editorInstance = window.open(url.toString(), '_blank');
            }
        },

        rename({ detail }) {
            const newName = detail.name;

            if (newName) {
                const cell = { ...this.localCell };

                cell.value = cell.value || {};
                cell.value.marketingEmailId = this.marketingEmailId;
                cell.value.name = newName;

                this.$emit('update-cell-name', { cell });
            }
        },

        saveCell() {
            const cell = { ...this.localCell };

            if (!cell.value.initialized) {
                const parent = findParent(cell.meta.parent, this.cells);

                if (parent.length > 0 && !parent[0].value.initialized) {
                    parent[0].value.initialized = '1';
                }
            }

            cell.value = cell.value || {};
            cell.value.marketingEmailId = this.marketingEmailId;
            this.$emit('update-cell-value', { cell, isReady: this.isReady, preventClose: true });
        },

        findEmailConfirmationGoal () {
            const targetCells = findOutgoingCells(this.localCell.meta.parent, this.cells);

            if (targetCells?.length > 0 && targetCells[0].meta.style === GOALS.GOAL_EMAIL_CONFIRM) {
                return targetCells[0];
            }

            return null;
        },

        initializeEmailConfirmationGoal (confirmLinkId) {
            const linkId = confirmLinkId || this.confirmLinkId;

            if (linkId && linkId !== '0') {
                const emailConfirmationGoal = this.findEmailConfirmationGoal();

                if (emailConfirmationGoal) {
                    if (confirmLinkId) {
                        emailConfirmationGoal.value.linkIds = [confirmLinkId];
                    }

                    emailConfirmationGoal.value.ready = this.isReady ? '1' : '0';
                    this.$emit('update-cell-value', { cell: emailConfirmationGoal, isReady: this.isReady, preventClose: true });
                }
            }
        },

        toggleReadyParentCell () {
            const emailConfirmationSequence = getCellById(this.localCell.meta.parent, this.cells);

            if (emailConfirmationSequence) {
                emailConfirmationSequence.value.ready = this.isReady ? '1' : '0';
                this.$emit('update-cell-value', {
                    cell: emailConfirmationSequence,
                    isReady: this.isReady,
                    preventClose: true,
                });
            }
        },
    },
};
</script>
